import { Event } from '@mirai/data-sources';
import { useLocale } from '@mirai/locale';
import { ServiceBooking } from '@mirai/services';
import { Button, Modal, Pressable, styles, Text, useDevice, View } from '@mirai/ui';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { RoomCard } from './Booking.RoomCard';
import { useSelectedRoomData } from './hooks';
import { ERROR } from '../../../Core.constants';
import { EVENT } from '../../helpers';
import { L10N } from '../Booking.l10n';
import * as style from '../Booking.module.css';

const ModalBoard = ({
  dataSource: { currency, details = [], id, modify: { board: { rooms = [] } = {} } = {} } = {},
  room,
  token,
  visible,
  onBack = () => {},
  onClose = () => {},
  onConfirm = () => {},
  ...others
}) => {
  const { isMobile } = useDevice();
  const { currencyFormat, translate } = useLocale();
  const {
    selectedRoom: selectedBoardRoom,
    selectedRoomData: { image, occupation, plan, rate, title } = {},
    showSelectedRoom,
  } = useSelectedRoomData({ details, room, rooms });

  const [busy, setBusy] = useState(false);
  const [value, setValue] = useState();

  const visiblePrevious = useRef(false);

  useEffect(() => {
    const isVisible = visible === true;
    const isOpening = isVisible && !visiblePrevious.current;

    visiblePrevious.current = isVisible;
    if (!isOpening) return;

    setValue(undefined);
  }, [visible]);

  const options = selectedBoardRoom?.options || [];
  const showBack = isMobile || showSelectedRoom;
  const getOptionPrice = ({ priceDifference } = {}) =>
    `${priceDifference > 0 ? '+' : ''}${currencyFormat({ currency, value: priceDifference })}`;

  const handleSubmit = async () => {
    if (!value) return;

    setBusy(true);
    const response = await ServiceBooking.modification.board
      .quote({ id, option: value.id, roomId: room.id, token })
      .catch(() => {
        Event.publish(EVENT.NOTIFICATION, {
          error: true,
          defaultMessage: translate(L10N.NOTIFICATION_ERROR[ERROR.UNKNOWN]),
        });
      })
      .finally(() => {
        setBusy(false);
      });

    if (!response) return;

    onClose();
    onConfirm({ ...response, room });
  };

  return (
    <Modal
      {...others}
      title={translate(L10N.ACTION_EDIT_BOARD)}
      visible={visible}
      onClose={onClose}
      className={styles(style.modalBoard, others.className)}
    >
      <View>
        {showSelectedRoom && (
          <RoomCard
            {...{ image, occupation, plan, rate, title }}
            className={style.selectedRoomCard}
            testId="booking-edit-board-room-card"
          />
        )}
        <View className={style.boardOptions}>
          <Text action bold>
            {translate(L10N.LABEL_SELECT_BOARD)}
          </Text>
          <View className={style.boardOptionList}>
            {options.map((option = {}, index) => (
              <Pressable
                key={option.id}
                className={styles(style.option, value?.id === option.id && style.optionSelected, style.boardOption)}
                onPress={() => setValue(option)}
                testId={`booking-edit-board-option-${index}`}
              >
                <View row className={style.boardOptionContent}>
                  <Text action>{option.name}</Text>
                  <Text action className={style.boardOptionPrice}>
                    {getOptionPrice(option)}
                  </Text>
                </View>
              </Pressable>
            ))}
          </View>
        </View>
        <View row className={style.footerActions}>
          {showBack && (
            <Button secondary onPress={onBack} testId="booking-edit-board-back">
              {translate(L10N.ACTION_BACK)}
            </Button>
          )}
          <Button busy={busy} disabled={!value} onPress={handleSubmit} testId="booking-edit-board-next">
            {translate(L10N.ACTION_NEXT)}
          </Button>
        </View>
      </View>
    </Modal>
  );
};

ModalBoard.displayName = 'Mirai:Core:Booking:ModalBoard';

ModalBoard.propTypes = {
  dataSource: PropTypes.shape({
    currency: PropTypes.string,
    details: PropTypes.arrayOf(PropTypes.shape({})),
    modify: PropTypes.shape({
      board: PropTypes.shape({
        rooms: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string,
            options: PropTypes.arrayOf(
              PropTypes.shape({
                id: PropTypes.number,
                name: PropTypes.string,
                priceDifference: PropTypes.number,
              }),
            ),
          }),
        ),
      }),
    }),
  }),
  room: PropTypes.shape({
    id: PropTypes.string,
    index: PropTypes.number,
  }),
  token: PropTypes.string,
  visible: PropTypes.bool,
  onBack: PropTypes.func,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
};

export { ModalBoard };
